import type { ThemeTokens } from '@cogo/react-renderer-v2'
import { COLOR_TOKEN_KEY_TO_CSS_VAR } from '@cogo/react-renderer'

export type RendererThemeTokens = ThemeTokens & {
  '--cogo-font-heading-url'?: string
  '--cogo-font-body-url'?: string
}

/**
 * Every name `getIsolatedAppStyle` would honour as a colour slot — BOTH
 * spellings it accepts, because its `resolveColorValue` reads
 * `colors[camelKey] ?? colors[cssVar]`.
 *
 * Derived from the writer's own table (`COLOR_TOKEN_KEY_TO_CSS_VAR` in
 * `@cogo/react-renderer`) rather than restated here: this set exists only to
 * decide "would the CSS-variable writer have emitted this name?", so the
 * answer must come from that writer. A hand-copied list is the drift that put
 * a project's palette on the floor in the first place.
 */
const COLOR_SLOT_NAMES: ReadonlySet<string> = new Set([
  ...Object.keys(COLOR_TOKEN_KEY_TO_CSS_VAR),
  ...Object.values(COLOR_TOKEN_KEY_TO_CSS_VAR),
])

function readRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
}

function hasKeys(value: unknown): value is Record<string, unknown> {
  const record = readRecord(value)
  return Boolean(record && Object.keys(record).length > 0)
}

/**
 * Colour slots written FLAT on the record instead of nested under `colors`.
 *
 * Both shapes are produced inside this repo — `computeThemeTokens()` returns
 * the nested `ThemeTokens`, while `toDesignSystemJson()` /
 * `encodeAppSpecDesignSystemTokens()` return the same palette flattened to one
 * level — and both end up in a project's `tokens`, through import bundles and
 * through direct project writes. Reading only the nested one discarded a whole
 * palette with no error and no diagnostic: the render simply fell back to
 * `DEFAULT_LIGHT_THEME`, which looks exactly like a project that never had a
 * design system.
 */
function liftFlatColors(source: Record<string, unknown>): Record<string, string> | undefined {
  const lifted: Record<string, string> = {}
  for (const [key, value] of Object.entries(source)) {
    if (!COLOR_SLOT_NAMES.has(key)) continue
    if (typeof value !== 'string' || !value.trim()) continue
    lifted[key] = value
  }
  return Object.keys(lifted).length > 0 ? lifted : undefined
}

/**
 * The pass-through channel: CSS custom properties the app declared for itself,
 * carried through untouched to `getIsolatedAppStyle`.
 *
 * The only thing checked here is that a value is a string — a TYPE boundary,
 * because `cssVars` is `Record<string, string>` and forwarding a number would
 * be a lie about the shape. Which NAMES are legal is deliberately NOT restated
 * here: that rule belongs to the writer that emits the variables, and a copy
 * of it in this reader is the same drift that put a project's palette on the
 * floor. Everything string-valued is forwarded; the renderer decides.
 */
function readCssVars(value: unknown): Record<string, string> | undefined {
  const record = readRecord(value)
  if (!record) return undefined
  const cssVars: Record<string, string> = {}
  for (const [name, entry] of Object.entries(record)) {
    if (typeof entry !== 'string') continue
    cssVars[name] = entry
  }
  return Object.keys(cssVars).length > 0 ? cssVars : undefined
}

export function toRendererThemeTokens(value: unknown): RendererThemeTokens | undefined {
  const source = readRecord(value)
  if (!source) return undefined

  const themeTokens: RendererThemeTokens = {}
  // Nested wins per slot: a record carrying both shapes is a record whose
  // `colors` is the deliberate one.
  const flatColors = liftFlatColors(source)
  const nestedColors = hasKeys(source.colors)
    ? source.colors as Record<string, string>
    : undefined
  if (flatColors || nestedColors) {
    themeTokens.colors = {
      ...flatColors,
      ...nestedColors,
    } as RendererThemeTokens['colors']
  }
  const cssVars = readCssVars(source.cssVars)
  if (cssVars) themeTokens.cssVars = cssVars
  if (hasKeys(source.spacing)) themeTokens.spacing = source.spacing as RendererThemeTokens['spacing']
  if (hasKeys(source.typography)) themeTokens.typography = source.typography as RendererThemeTokens['typography']
  if (typeof source.radius === 'string') {
    themeTokens.radius = source.radius
  } else if (hasKeys(source.radius)) {
    themeTokens.radius = source.radius as RendererThemeTokens['radius']
  }
  if (hasKeys(source.shadow)) themeTokens.shadow = source.shadow as RendererThemeTokens['shadow']
  if (hasKeys(source.fonts)) themeTokens.fonts = source.fonts as RendererThemeTokens['fonts']
  if (typeof source['--cogo-font-heading-url'] === 'string') {
    themeTokens['--cogo-font-heading-url'] = source['--cogo-font-heading-url']
  }
  if (typeof source['--cogo-font-body-url'] === 'string') {
    themeTokens['--cogo-font-body-url'] = source['--cogo-font-body-url']
  }
  if (typeof source.fontFamily === 'string') themeTokens.fontFamily = source.fontFamily
  if (typeof source.fontWeightDefault === 'number') themeTokens.fontWeightDefault = source.fontWeightDefault

  return Object.keys(themeTokens).length > 0 ? themeTokens : undefined
}
